import { TooltipContent } from '@radix-ui/react-tooltip';
import { accentColors, Container, lighterVariations, otherColors, secondaryColors } from '@tryhackme/thm-ui-components';
import styled, { keyframes } from 'styled-components';

const scroll = keyframes`
  from {
    transform: translateX( 0% );
  }
  to {
    transform: translateX(-50%);
  }
`;

export const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.8rem;
  width: 100%;
  max-width: 131.9rem;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    gap: 1.6rem;
  }
`;

export const StyledHeading = styled.h2`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: 1.8rem;
  line-height: 1.257;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.main};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: 1.6rem;
  }
`;

export const StyledRatingBadgesContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1.33rem;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    flex-direction: column;
    gap: 1rem;
  }
`;

export const StyledCompaniesWrapper = styled.div`
  width: 100%;
  padding: 5rem 0;
  position: relative;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    padding: 3rem 2rem 4rem;
  }
`;

export const StyledCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow-x: hidden;
  overflow-y: visible;
  height: 8rem;
  

  /* Fading effect on the left */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 15rem;
    height: 100%;
    background: linear-gradient(to right, ${secondaryColors.black}, transparent);
    z-index: 2;
    pointer-events: none;
  }

  /* Fading effect on the right */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 15rem;
    height: 100%;
    background: linear-gradient(to left, ${secondaryColors.black}, transparent);
    z-index: 2;
    pointer-events: none;
  }

  @media screen and (max-width: ${({ theme }) => theme.sizes.lg}) {
    &::before,
    &::after {
      width: 8rem;
    }
  }
`;

export const StyledCarouselTrack = styled.div`
  display: flex;
  gap: 9rem;
  animation: ${scroll} 50s linear infinite;
  width: fit-content;
  align-items: center;

  /* Pause animation on hover */
  &:hover {
    animation-play-state: paused;

    @media (max-width: ${({ theme }) => theme.sizes.md}) {
      animation-play-state: running;
    }
  }

  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    gap: 6rem;
    animation-duration: 40s;
  }

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    gap: 4rem;
  }
`;

export const StyledCompanyImage = styled.img`
  height: 3.8rem;
  object-fit: contain;
  align-self: center;
  flex-shrink: 0;
  min-width: 15rem;
  opacity: 0.8;

  &[alt='ThreatLocker'] {
    height: 1.9rem
  }
`;

export const StyledCompanyLogoCaseStudyWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    ${StyledCompanyImage} {
      opacity: 0.9;
    }
  }

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    display: none;
  }
`;

export const StyledCaseStudyBadge = styled.div`
  position: absolute;
  bottom: -3.4rem;
  right: -2rem;
  background: ${lighterVariations.blue.darker};
  color: ${secondaryColors.white};
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: 1.4rem;
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  padding: 0.4rem 0.8rem;
  border-radius: 1.5rem;
  box-shadow: ${({ theme }) => theme.boxShadow.main};
  white-space: nowrap;
  z-index: 1;
  transition: background 0.2s ease-in-out;

  :hover {
    background: ${accentColors.info.dark};
    color: ${otherColors.textL};
  }

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    display: none;
  }
`;

export const StyledCaseStudyTooltipContent = styled(TooltipContent)`
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
  width: 52.1rem;
  max-width: 90vw;
  background: ${secondaryColors.secondary.light};
  padding: 3.2rem;
  border-radius: 0.5rem;
  box-shadow: 0px 0.79rem 2.6rem 0px rgba(0, 0, 0, 0.6);
  z-index: 1001;
  animation: slideUpAndFade 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes slideUpAndFade {
    from {
      opacity: 0;
      transform: translateY(0.4rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    padding: 2rem;
    gap: 1.4rem;
  }
`;

export const StyledTooltipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.8rem;

  img {
    max-width: 13.839rem;
    max-height: 3.412rem;
    object-fit: contain;
  }

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    flex-direction: column;
    gap: 1rem;

    img {
      max-width: 10rem;
    }
  }
`;

export const StyledIndustryTag = styled.span`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  color: #878fa2;
  flex-shrink: 0;

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.small};
  }
`;

export const StyledTestimonialQuote = styled.p`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: ${({ theme }) => theme.fonts.sizes.medium};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  line-height: 1.257;
  color: #b8bec9;
  margin: 0 0 1.8rem 0;

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.default};
    margin-bottom: 1.2rem;
  }
`;

export const StyledTestimonialName = styled.p`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: ${({ theme }) => theme.fonts.sizes.medium};
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  line-height: 1.257;
  color: ${({ theme }) => theme.colors.text.main};
  margin: 0 0 0.6rem 0;

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.default};
  }
`;

export const StyledTestimonialAuthor = styled.p`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: ${({ theme }) => theme.fonts.sizes.medium};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  line-height: 1.257;
  color: ${({ theme }) => theme.colors.text.main};
  margin: 0;

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.default};
  }
`;
